import { FirebaseMessaging } from '@capacitor-firebase/messaging';
import { Capacitor } from '@capacitor/core';
import { initializeApp, getApps } from 'firebase/app';

const VAPID_KEY = process.env.REACT_APP_FIREBASE_VAPID_KEY;
const FCM_TOKEN_KEY = 'fcm_token';

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
};

class FirebaseMessagingService {
  constructor() {
    this.initialized = false;
    this.platform = Capacitor.getPlatform();
    this.token = null;
    this.listeners = [];
    this.readyPromise = null;
  }

  /** Kept as a plain wrapper so index.js's fire-and-forget `initialize()` call stays unchanged, while callers that need the resolved token (DeviceTokenRegistrar) can await the very same run instead of racing it with a second one. */
  initialize() {
    if (!this.readyPromise) this.readyPromise = this._doInitialize();
    return this.readyPromise;
  }

  async _doInitialize() {
    try {
      // En web, el plugin delega en la Firebase JS SDK que debe inicializarse primero
      if (this.platform === 'web' && getApps().length === 0) {
        initializeApp(firebaseConfig);
      }

      const { receive } = await FirebaseMessaging.requestPermissions();
      if (receive !== 'granted') return;

      const tokenOptions = this.platform === 'web' ? { vapidKey: VAPID_KEY } : {};
      const { token } = await FirebaseMessaging.getToken(tokenOptions);
      this.token = token;

      if (token) {
        localStorage.setItem(FCM_TOKEN_KEY, token);
        console.info('[FCM] Token (úsalo para probar notificaciones):', token);
      }

      // Suscribir al topic general (solo Android/iOS — web no soporta topics)
      if (this.platform !== 'web') {
        await FirebaseMessaging.subscribeToTopic({ topic: 'all_users' });
      }

      this._registerListeners();
      this.initialized = true;
    } catch (error) {
      console.error('[FCM] Error al inicializar:', error);
    }
  }

  _registerListeners() {
    this.listeners.push(
      FirebaseMessaging.addListener('notificationReceived', (event) => {
        // Notificación recibida con la app en primer plano
        console.log('[FCM] Notificación en foreground:', event.notification);
      })
    );

    this.listeners.push(
      FirebaseMessaging.addListener('notificationActionPerformed', (event) => {
        // Usuario pulsó una notificación (app abierta desde background)
        console.log('[FCM] Notificación pulsada:', event.notification);
      })
    );
  }

  getToken() {
    return this.token || localStorage.getItem(FCM_TOKEN_KEY);
  }

  /** Awaits the in-flight/completed initialize() run (index.js already kicked it off at app boot) before reading the token — avoids a race where a caller reads getToken() before permission was even requested. */
  async getTokenAsync() {
    await this.initialize();
    return this.getToken();
  }

  /**
   * Uploads this device's FCM token to the backend so it can push to this
   * specific account (device-token.repository.ts) — until now the token
   * only ever reached this device's own console log. Safe to call more than
   * once (e.g. on every login): the backend upserts by token.
   */
  async registerWithBackend(authFetch) {
    const token = await this.getTokenAsync();
    if (!token) return;
    try {
      await authFetch('/notifications/device-token', { method: 'POST', body: { token, platform: this.platform } });
    } catch (error) {
      console.error('[FCM] Error al registrar el token en el backend:', error);
    }
  }
}

export const firebaseMessagingService = new FirebaseMessagingService();
export default firebaseMessagingService;
